import React, { ReactNode, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { useSlimDesign } from 'utils/useSlimDesign';

import { ClientPortalConfigurationsSelector } from 'store/clientPortalChats';
import { ThunkGetClientPortalConfigurations } from 'store/clientPortalChats/thunks';
import { CallScribeAccessSelector } from 'store/callScribe/selectors';
import { ThunkGetCallScribeAccess } from 'store/callScribe/thunks';
import { ConfigurationSelector } from 'store/configuration';
import { PodFeaturesSelector, ThunkGetPodFeatures } from 'store/podFeatures';
import { MyPricePlanSelector } from 'store/pricePlan/selectors';
import { ThunkGetPricePlans } from 'store/pricePlan/thunks';
import {
  scheduleAssistTabVisibleSelector,
  ThunkGetScheduleAssistAccess,
} from 'store/scheduleAssistAccess';
import { hasWhatsAppSettingsSelector } from 'store/whatsAppMessaging';
import { ThunkGetWhatsAppSettings } from 'store/whatsAppMessaging/thunks';

import { ReactComponent as ChatsIcon } from 'assets/icons/chats.svg';
import { ReactComponent as TechVerificationIcon } from 'assets/icons/person_search.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg';
import { ReactComponent as EndUserVerificationIcon } from 'assets/icons/shield_person.svg';
import { ReactComponent as ApprovalRequestIcon } from 'assets/icons/shield_checked.svg';
import { ReactComponent as SMSIcon } from 'assets/icons/sms.svg';
import { ReactComponent as SecureDatSharingIcon } from 'assets/icons/sync.svg';
import { ReactComponent as TeamsIcon } from 'assets/icons/teams_flat.svg';
import { ReactComponent as TicketIcon } from 'assets/icons/ticket.svg';
import { ReactComponent as WhatsappIcon } from 'assets/icons/whatsapp.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import whiteWolfLogo from 'assets/logo/whiteWolfLogo.png';

import { PodFeature } from 'services/webExtension/types';

import { Integrations } from 'constants/integrations';
import { Pages } from 'constants/pages';
import { PricePlans, resolvePricePlan } from 'constants/pricePlans';

const ScheduleIcon = CalendarIcon;

interface IMenuItem {
  text: string;
  icon: ReactNode;
  page: Pages;
  allowedPsa: Integrations[];
  screens: string[];
  pricePlans: PricePlans[];
  feature?: PodFeature;
}
const allIntegrations = [
  Integrations.ConnectWise,
  Integrations.Halo,
  Integrations.Autotask,
  Integrations.Kaseya,
  Integrations.Syncro,
  Integrations.ServiceNow,
  Integrations.SuperOps,
  Integrations.Zendesk,
  Integrations.Microsoft,
  Integrations.ImportIntegration,
  Integrations.FreshService,
];

const allPricePlans = [
  PricePlans.Pro,
  PricePlans.Assist,
  PricePlans.VerificationPlus,
  PricePlans.Free,
];

export const menuItems: IMenuItem[] = [
  {
    text: 'End User Verification',
    icon: <EndUserVerificationIcon />,
    page: Pages.EndUserVerification,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
  },
  {
    text: 'CallScribe',
    icon: <PhoneIcon />,
    page: Pages.CallScribe,
    allowedPsa: [Integrations.ConnectWise, Integrations.Halo, Integrations.Autotask],
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
  },
  {
    text: 'Chats',
    icon: <ChatsIcon />,
    page: Pages.Chats,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'SMS Messaging',
    icon: <SMSIcon />,
    page: Pages.SMSMessaging,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'WhatsApp Messaging',
    icon: <WhatsappIcon />,
    page: Pages.WhatsAppMessaging,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'Client Portal Chats',
    icon: <ChatsIcon />,
    page: Pages.ClientPortalChats,
    allowedPsa: [Integrations.ConnectWise, Integrations.Halo, Integrations.Autotask],
    screens: ['ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'Alpha',
    icon: <img src={whiteWolfLogo} alt="" aria-hidden="true" width={32} height={32} />,
    page: Pages.Alpha,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
    feature: 'alpha',
  },
  {
    text: 'Secure Data Sharing',
    icon: <SecureDatSharingIcon />,
    page: Pages.SecureDataSharing,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
    feature: 'secureDataSharing',
  },
  {
    text: 'Manager Approval',
    icon: <ApprovalRequestIcon />,
    page: Pages.ApprovalRequest,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.VerificationPlus, PricePlans.Assist, PricePlans.Pro],
    feature: 'approvalRequest',
  },
  {
    text: 'Technician Verification',
    icon: <TechVerificationIcon />,
    page: Pages.TechnicianVerification,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: [PricePlans.VerificationPlus, PricePlans.Assist, PricePlans.Pro],
    feature: 'technicianVerification',
  },
  {
    text: 'Quick Ticket',
    icon: <TicketIcon />,
    page: Pages.QuickTicket,
    allowedPsa: [Integrations.ConnectWise],
    screens: ['contact'],
    pricePlans: [PricePlans.Pro],
    feature: 'quickTickets',
  },
  {
    text: 'MS Teams Chats',
    icon: <TeamsIcon />,
    page: Pages.MSTeamsChats,
    allowedPsa: [Integrations.ConnectWise],
    screens: ['ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'Schedule Assist',
    icon: <ScheduleIcon />,
    page: Pages.ScheduleAssist,
    allowedPsa: allIntegrations,
    // 'contact' included so a tech can book straight from a contact panel; the booking
    // is ticketless there (no PSA ticket note is written).
    screens: ['ticket', 'project_ticket', 'contact'],
    pricePlans: [PricePlans.Assist, PricePlans.Pro],
  },
];

export const useAllowedMenuItems = (): IMenuItem[] => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(ConfigurationSelector);
  const myPlan = useAppSelector(MyPricePlanSelector);
  const clientPortalConfigs = useAppSelector(ClientPortalConfigurationsSelector);
  const hasWhatsAppSettings = useAppSelector(hasWhatsAppSettingsSelector);
  const callScribeAccess = useAppSelector(CallScribeAccessSelector);
  // Schedule Assist tab shows whenever the tenant module is on — user-level
  // denials (not Ready / disabled) render an actionable guide state in the panel.
  const scheduleAssistTabVisible = useAppSelector(scheduleAssistTabVisibleSelector);
  const podFeatures = useAppSelector(PodFeaturesSelector);
  const isSlim = useSlimDesign();

  const slimHiddenPages = [Pages.SMSMessaging, Pages.WhatsAppMessaging, Pages.ClientPortalChats];

  const psaFiltered = useMemo(
    () =>
      menuItems
        .filter(i => config?.psa && i.allowedPsa.includes(config?.psa))
        .filter(i => (config?.screen ? i.screens.includes(config?.screen) : true))
        .filter(i => (isSlim ? !slimHiddenPages.includes(i.page) : i.page !== Pages.Chats)),
    [config, isSlim],
  );

  const featureFiltered = useMemo(
    () => psaFiltered.filter(i => (i.feature ? podFeatures?.[i.feature] === true : true)),
    [psaFiltered, podFeatures],
  );

  const haveClientPortalConfig = useMemo(() => {
    return clientPortalConfigs.some(i => i.crmId === config?.psaId);
  }, [clientPortalConfigs, config?.psaId]);

  useEffect(() => {
    if (config?.psaId) {
      Promise.all([
        dispatch(ThunkGetPricePlans()),
        dispatch(ThunkGetClientPortalConfigurations()),
        dispatch(ThunkGetWhatsAppSettings()),
        dispatch(ThunkGetCallScribeAccess()),
        dispatch(ThunkGetScheduleAssistAccess()),
        dispatch(ThunkGetPodFeatures()),
      ]);
    }
  }, [config?.psaId, dispatch]);

  // if USER don't have subscription don't show any items
  if (!myPlan?.isSubscriptionEnabled) {
    return [];
  }

  // if USER has DEMO or OBSOLETE subscription return all
  if (myPlan?.isDemoSubscription ?? myPlan?.isSubscriptionObsolete) {
    return featureFiltered
      .filter(i => (i.page === Pages.ScheduleAssist ? scheduleAssistTabVisible : true));
  }

  return featureFiltered
    // An unresolved plan name must not fail open into the full menu — fall back to the
    // lowest tier so we never surface options the tenant has not subscribed to.
    .filter(i => i.pricePlans.includes(resolvePricePlan(myPlan.name)))
    .filter(i => (i.page === Pages.ClientPortalChats ? haveClientPortalConfig : true))
    .filter(i => (i.page === Pages.WhatsAppMessaging ? hasWhatsAppSettings : true))
    .filter(i => (i.page === Pages.CallScribe ? !!callScribeAccess?.hasAccess : true))
    .filter(i => (i.page === Pages.ScheduleAssist ? scheduleAssistTabVisible : true))
    .filter(i => i.page !== Pages.MSTeamsChats);
};
